import { Component, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { JsonReaderService } from 'src/app/services/json-reader.service';

interface Branch {
  id: number;
  image: string;
  location: string;
  name: string;
  address: string;
  landmark: string;
  business_hours: string;
  landline: string[];
  textline: string[];
  toll_fee: string[];
  email: string;
  agent?: string;
  contact_number?: string[];
  lat: number;
  lng: number;
}

@Component({
  selector: 'app-branch',
  templateUrl: './branch.component.html',
  styleUrls: ['./branch.component.scss'],
})
export class BranchComponent implements OnInit {
  readonly fallbackBranchImage =
    'https://us.logocargo.com/assets/img/thumbnail/new-logo-thumbnail.jpg';
  branches: Branch[] = [];
  filteredBranches: Branch[] = [];
  selectedBranch?: Branch;
  branchesWithDistance: any[] = [];
  mapCenter = { lat: 38.20772792851379, lng: -122.14060218770402 };
  zoom = 12;
  currUser: Observable<any>;
  searchQuery: string = '';
  currentLat?: number;
  currentLng?: number;

  constructor(
    private authService: AuthService,
    private title: Title,
    private meta: Meta,
    private jsonReaderService: JsonReaderService
  ) {}

  private getDistanceFromLatLng(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371;
    const toRad = (value: number) => (value * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private calculateAndSortBranches(): void {
    this.branchesWithDistance = this.branches
      .map((branch) => ({
        ...branch,
        distance: this.getDistanceFromLatLng(
          this.currentLat!,
          this.currentLng!,
          branch.lat,
          branch.lng
        ),
      }))
      .sort((a, b) => a.distance - b.distance);

    this.filteredBranches = [...this.branchesWithDistance];
  }

  private resetBranchResults(): void {
    this.filteredBranches = [...this.branchesWithDistance];
  }

  ngOnInit(): void {
    this.currUser = this.authService.user;

    this.title.setTitle('Branches | LOGO');
    this.meta.updateTag({
      name: 'description',
      content: 'LOGO - Branches',
    });

    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(
        (position) => {
          this.currentLat = position.coords.latitude;
          this.currentLng = position.coords.longitude;
          if (this.currentLat && this.currentLng && this.branches.length > 0) {
            this.calculateAndSortBranches();
          }

          this.mapCenter = { lat: this.currentLat, lng: this.currentLng };
          this.zoom = 13;
          console.log('Map centered to browser location:', this.mapCenter);
        },
        (error) => {
          console.warn('Unable to get current location:', error);

          this.mapCenter = { lat: 38.20772792851379, lng: -122.14060218770402 };
        }
      );
    } else {
      console.warn('Geolocation is not supported by this browser.');
      this.mapCenter = { lat: 38.20772792851379, lng: -122.14060218770402 };
    }

    this.jsonReaderService.readJsonFile('branches').subscribe(
      (result: any) => {
        this.branches = result || [];

        if (this.currentLat && this.currentLng) {
          this.calculateAndSortBranches();
        } else {
          this.branchesWithDistance = [...this.branches];
          this.resetBranchResults();
        }
      },
      (error: any) => {
        console.error('Error loading branches:', error);
      }
    );
  }

  selectBranch(branch: any): void {
    console.log(branch);
    this.selectedBranch = branch;
    this.mapCenter = { lat: branch.lat, lng: branch.lng };
    this.zoom = 15;
  }

  setBranchImageFallback(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    image.src = this.fallbackBranchImage;
  }

  searchBranches(): void {
    const query = this.searchQuery.toLowerCase().trim();

    if (!query) {
      this.resetBranchResults();
      return;
    }

    this.filteredBranches = this.branchesWithDistance.filter((branch) => {
      return (
        branch.name.toLowerCase().includes(query) ||
        branch.location.toLowerCase().includes(query) ||
        branch.address?.toLowerCase().includes(query) ||
        branch.landmark?.toLowerCase().includes(query)
      );
    });
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this.searchBranches();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.resetBranchResults();
  }

  openInGoogleMaps(lat: number, lng: number) {
    if (this.currentLat && this.currentLng) {
      const url = `https://www.google.com/maps/dir/?api=1&origin=${this.currentLat},${this.currentLng}&destination=${lat},${lng}&travelmode=driving`;
      window.open(url, '_blank');
    } else {
      alert(
        'Unable to get your current location. Please allow location access.'
      );
    }
  }
}
